import React, { useEffect } from 'react';
import { Form, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getAssets } from '@/api/assets';
import { useAuth } from '@/contexts/AuthContext';
import type { Asset } from '@/types/resources/asset';

interface AssetSelectorProps {
  disabled?: boolean;
  onAssetDataChange?: (asset: { id: string; name: string } | null) => void;
}

export const AssetSelector: React.FC<AssetSelectorProps> = ({
  disabled = false,
  onAssetDataChange
}) => {
  const { t } = useTranslation(['assets', 'common']);
  const { firestoreUser } = useAuth();
  const form = Form.useFormInstance();

  // Fetch assets
  const { data: assetsData, error } = useQuery({
    queryKey: ['assets', firestoreUser?.workspace?.id],
    queryFn: () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getAssets({ 
        firestoreUser,
        pageSize: 100 // Get all assets for selector
      });
    },
    enabled: !!firestoreUser?.workspace?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Initialize asset data from form values when component mounts or form values change
  useEffect(() => {
    const assetId = form.getFieldValue('assetId');
    if (assetId && assetsData?.assets) {
      const asset = assetsData.assets.find((a: Asset) => a.id === assetId);
      onAssetDataChange?.(asset ? { 
        id: asset.id, 
        name: asset.name 
      } : null);
    }
  }, [form, assetsData, onAssetDataChange]);

  const handleAssetChange = (assetId: string) => {
    // Provide asset data to parent
    if (assetId) {
      const asset = assetsData?.assets?.find((a: Asset) => a.id === assetId);
      onAssetDataChange?.(asset ? { 
        id: asset.id, 
        name: asset.name 
      } : null);
    } else {
      onAssetDataChange?.(null);
    }
  };

  const assetOptions = assetsData?.assets?.map((asset: Asset) => ({
    value: asset.id,
    label: asset.name,
  })) || [];

  // Show error state if API fails
  if (error) {
    return (
      <Form.Item
        name="assetId"
        label={t('assets:fields.asset', 'Asset')}
      >
        <Select
          placeholder={t('assets:fields.selectAsset')}
          disabled={true}
          options={[]}
        />
      </Form.Item>
    );
  }

  return (
    <Form.Item
      name="assetId"
      label={t('assets:fields.asset', 'Asset')}
    >
      <Select
        placeholder={t('assets:fields.selectAsset')}
        onChange={handleAssetChange}
        disabled={disabled}
        allowClear
        options={assetOptions}
        showSearch
        filterOption={(input, option) =>
          (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
        }
      />
    </Form.Item>
  );
}; 